import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChefHat, Camera, Check, ChevronRight, ChevronLeft, 
  Scale, Save, BookOpen, FileText,
  X, Loader2, Zap, PackageCheck, Pencil, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import DateInput from "../components/DateInput";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useFeature } from "../context/FeatureContext";
import { useAuth } from "../context/AuthContext";
import { DURATE_OPZIONI, toSmall, fromSmall, formatSmallQuantity, updateQuantityInput, isIngredienteCompleto, lottiDisponibiliPerIngrediente } from "./prepara/preparaUtils";
import RicettaInlineForm from "./prepara/RicettaInlineForm";
import { LightRecipeEditorDialog } from "./prepara/LightRecipeEditorDialog";
import { LightRecipeTrash } from "./prepara/LightRecipeTrash";
import FotoSceltaDialog from "./prepara/FotoSceltaDialog";
import ConfermaSovrascritturaDialog from "./prepara/ConfermaSovrascritturaDialog";
import { LottiEsistentiDialog } from "./prepara/LottiEsistentiDialog";
import FatturaSheet from "./lotti/FatturaSheet";
import { fileUrl } from "../lib/fileUrl";
import { useLocalFilePreviews } from "../hooks/useLocalFilePreviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreparaDialog = ({ 
  open, 
  onClose, 
  localeId, 
  getAuthHeaders, 
  operatore,
  onSuccess,
  ricettaIniziale,
  lottiDisponibili = []
}) => {
  const [step, setStep] = useState('select');
  const [loading, setLoading] = useState(false);
  const { isAdmin } = useAuth();
  const { hasFeature } = useFeature();
  // Le ricette light esistono SOLO per chi NON ha il pacchetto Acquisti.
  // Con Acquisti attivo si crea sempre in modalità completa (ingredienti DB + food cost).
  const hasAcquisti = hasFeature("prodotti_catalogo");
  const [ricette, setRicette] = useState([]);
  const fetchRequestRef = useRef(0);
  const currentLocaleRef = useRef(localeId);
  const currentOpenRef = useRef(open);
  currentLocaleRef.current = localeId;
  currentOpenRef.current = open;
  const [recipeContextError, setRecipeContextError] = useState(null);
  const [editorRecipe, setEditorRecipe] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archivingLight, setArchivingLight] = useState(false);
  const [trashedRecipes, setTrashedRecipes] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [ingredientiDisponibili, setIngredientiDisponibili] = useState([]);
  const [ricettaSelezionata, setRicettaSelezionata] = useState(null);
  const [nomePreparazione, setNomePreparazione] = useState("");
  const [ingredientiPrepara, setIngredientiPrepara] = useState([]);
  const [currentIngredienteIndex, setCurrentIngredienteIndex] = useState(0);
  const [fotoDialogOpen, setFotoDialogOpen] = useState(false);
  const [lottiDialogOpen, setLottiDialogOpen] = useState(false);
  const [fotoBase64, setFotoBase64] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // ─── Fattura per ingrediente (facoltativa) ────────────────────
  const fatturaFileInputRef = useRef(null);
  const [fatturaSheetOpen, setFatturaSheetOpen] = useState(false);
  const [fatturaStorico, setFatturaStorico] = useState([]);
  const [fatturaUploading, setFatturaUploading] = useState(false);
  const { urls: fatturaPreviewUrls, remember: rememberFatturaPreview, clear: clearFatturaPreviews, get: getFatturaPreview } = useLocalFilePreviews();

  useEffect(() => { if (!open) clearFatturaPreviews(); }, [open, clearFatturaPreviews]);

  const fetchFatturaStorico = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fatture-storico?limit=10`, { headers: getAuthHeaders() });
      setFatturaStorico(res.data?.items || []);
    } catch { /* silent */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open) fetchFatturaStorico();
  }, [open, fetchFatturaStorico]);

  const handleFatturaNuovoScatto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFatturaUploading(true);
    try {
      const { compressToWebP } = await import("../utils/imageCompressor");
      const { file: webpFile, preview } = await compressToWebP(file, 0.85);
      const formData = new FormData();
      formData.append("file", webpFile);
      const res = await axios.post(
        `${API}/upload-fattura-image?locale_id=${localeId || "default"}`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      const path = res.data?.path;
      if (path) {
        rememberFatturaPreview(path, webpFile, preview);
        setIngredientiPrepara(prev => prev.map((ing, idx) =>
          idx === currentIngredienteIndex ? { ...ing, fattura_immagine_path: path } : ing
        ));
        setFatturaStorico(prev => [{ path, pending: true, last_used_at: new Date().toISOString() },
          ...prev.filter(x => x.path !== path)].slice(0, 10));
        toast.success("Fattura caricata");
        setFatturaSheetOpen(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "sconosciuto";
      toast.error(`Errore caricamento fattura: ${detail}`);
    } finally {
      setFatturaUploading(false);
      if (fatturaFileInputRef.current) fatturaFileInputRef.current.value = "";
    }
  };

  const handleFatturaSelectFromStorico = (path) => {
    setIngredientiPrepara(prev => prev.map((ing, idx) =>
      idx === currentIngredienteIndex ? { ...ing, fattura_immagine_path: path } : ing
    ));
    setFatturaSheetOpen(false);
    toast.success("Fattura selezionata dallo storico");
  };

  const handleFatturaRemove = () => {
    setIngredientiPrepara(prev => prev.map((ing, idx) =>
      idx === currentIngredienteIndex ? { ...ing, fattura_immagine_path: "" } : ing
    ));
  };
  
  // Data scadenza del lotto (calcolata automaticamente come minimo tra ingredienti)
  const [dataScadenzaLotto, setDataScadenzaLotto] = useState("");
  const [durataGiorni, setDurataGiorni] = useState(""); // es. "10" oppure "custom"

  // DURATE_OPZIONI, toSmall, isIngredienteCompleto importati da ./prepara/preparaUtils

  const handleDurataChange = (giorni) => {
    setDurataGiorni(giorni);
    if (giorni && giorni !== "custom") {
      const data = new Date();
      data.setDate(data.getDate() + parseInt(giorni));
      setDataScadenzaLotto(data.toISOString().split('T')[0]);
    }
  };

  const handleDataScadenzaChange = (dateStr) => {
    setDataScadenzaLotto(dateStr);
    if (dateStr) {
      const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
      const scad = new Date(dateStr); scad.setHours(0, 0, 0, 0);
      const diff = Math.ceil((scad - oggi) / (1000 * 60 * 60 * 24));
      setDurataGiorni(DURATE_OPZIONI.includes(diff) ? String(diff) : "custom");
    }
  };

  // Modal sovrascrittura ricetta
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'overwrite' | 'new' | 'skip'
  
  // Creazione ricetta light inline
  const [lightMode, setLightMode] = useState(false);
  const [lightNome, setLightNome] = useState("");
  const [lightComponenti, setLightComponenti] = useState([]);
  const [savingLight, setSavingLight] = useState(false);

  // Quantità realmente preparata + udm (override del calcolo automatico)
  const [quantitaPreparata, setQuantitaPreparata] = useState("");
  const [unitaMisuraLotto, setUnitaMisuraLotto] = useState("kg");
  const [qpManuallyEdited, setQpManuallyEdited] = useState(false);

  // Calcolo automatico peso finale dagli ingredienti (somma quantita * (1 - calo/100))
  const pesoFinaleCalcolatoGrammi = ingredientiPrepara.reduce((acc, ing) => {
    const q = parseFloat(ing.quantita_grammi) || 0;
    const calo = parseFloat(ing.calo_cottura_percentuale) || 0;
    return acc + q * (1 - calo / 100);
  }, 0);
  const pesoFinaleCalcolatoKg = Math.round((pesoFinaleCalcolatoGrammi / 1000) * 1000) / 1000;

  // Quando si entra nello step 'salva' o cambiano ingredienti, ricalcola default qty
  useEffect(() => {
    if (step !== "salva") return;
    if (qpManuallyEdited) return;
    if (unitaMisuraLotto === "kg") setQuantitaPreparata(String(pesoFinaleCalcolatoKg));
    else if (unitaMisuraLotto === "g") setQuantitaPreparata(String(Math.round(pesoFinaleCalcolatoGrammi)));
    // lt/pz: no default automatico (l'operatore inserisce a mano)
  }, [step, pesoFinaleCalcolatoKg, pesoFinaleCalcolatoGrammi, unitaMisuraLotto, qpManuallyEdited]);
  
  useEffect(() => {
    if (open && localeId) {
      resetState();
      fetchData();
    } else {
      fetchRequestRef.current += 1;
    }
  }, [open, localeId]);

  const resetState = () => {
    setStep('select');
    setEditorRecipe(null);
    setArchiveTarget(null);
    setTrashedRecipes([]);
    setRicettaSelezionata(null);
    setRecipeContextError(null);
    setNomePreparazione("");
    setIngredientiPrepara([]);
    setCurrentIngredienteIndex(0);
    setDataScadenzaLotto("");
    setDurataGiorni("");
    setConfirmOverwriteOpen(false);
    setLottiDialogOpen(false);
    setPendingAction(null);
    setLightMode(false);
    setLightNome("");
    setLightComponenti([]);
    setQuantitaPreparata("");
    setUnitaMisuraLotto("kg");
    setQpManuallyEdited(false);
  };

  const fetchData = async () => {
    const requestNumber = ++fetchRequestRef.current;
    const requestedLocale = localeId;
    try {
      const [ricetteRes, ingredientiRes] = await Promise.all([
        axios.get(`${API}/ricette?locale_id=${requestedLocale}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ingredienti?locale_id=${requestedLocale}`, { headers: getAuthHeaders() })
      ]);
      if (requestNumber !== fetchRequestRef.current ||
          requestedLocale !== currentLocaleRef.current || !currentOpenRef.current) return false;
      // Mostra ricette tracciabili (light sono sempre tracciabili)
      const ricetteTracciabili = ricetteRes.data.filter(r =>
        r.locale_id === requestedLocale && r.richiede_tracciabilita === true && r.is_archived !== true
      );
      // Ordina: light prima, poi complete
      ricetteTracciabili.sort((a, b) => {
        if (a.is_light && !b.is_light) return -1;
        if (!a.is_light && b.is_light) return 1;
        return a.nome.localeCompare(b.nome);
      });
      setRicette(ricetteTracciabili);
      setIngredientiDisponibili(ingredientiRes.data);
      if (ricettaIniziale) {
        const ricetta = ricetteTracciabili.find(r => r.id === ricettaIniziale);
        if (ricetta) selezionaRicetta(ricetta);
      }
      return true;
    } catch {
      if (requestNumber === fetchRequestRef.current && currentOpenRef.current) {
        toast.error("Errore nel caricamento dati");
      }
      return false;
    }
  };

  const openLightTrash = async () => {
    if (!isAdmin || hasAcquisti) return;
    setStep('trash');
    setTrashLoading(true);
    try {
      const res = await axios.get(`${API}/ricette-light/cestino?locale_id=${encodeURIComponent(localeId)}`, { headers: getAuthHeaders() });
      setTrashedRecipes(res.data);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Impossibile aprire il Cestino');
      setStep('select');
    } finally {
      setTrashLoading(false);
    }
  };

  const archiveLight = async () => {
    if (!archiveTarget || archivingLight) return;
    setArchivingLight(true);
    try {
      await axios.post(`${API}/ricette-light/${archiveTarget.id}/archive`, { locale_id: localeId }, { headers: getAuthHeaders() });
      setRicette(previous => previous.filter(recipe => recipe.id !== archiveTarget.id));
      toast.success('Ricetta spostata nel Cestino. I lotti esistenti sono rimasti invariati.');
      setArchiveTarget(null);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Impossibile eliminare la ricetta');
    } finally {
      setArchivingLight(false);
    }
  };

  const restoreLight = async (recipe) => {
    if (restoringId) return;
    setRestoringId(recipe.id);
    try {
      await axios.post(`${API}/ricette-light/${recipe.id}/restore`, { locale_id: localeId }, { headers: getAuthHeaders() });
      setTrashedRecipes(previous => previous.filter(item => item.id !== recipe.id));
      await fetchData();
      toast.success('Ricetta ripristinata');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Impossibile ripristinare la ricetta');
    } finally {
      setRestoringId(null);
    }
  };

  const selezionaRicetta = (ricetta) => {
    if (ricetta.locale_id !== localeId) {
      setRecipeContextError("La ricetta non appartiene alla sede selezionata. Controlla il locale prima di preparare il lotto.");
      return;
    }
    setRecipeContextError(null);
    setRicettaSelezionata(ricetta);
    setNomePreparazione(ricetta.nome);
    const ingredienti = ricetta.ingredienti
      .filter(ing => !ing.is_acqua)
      .map(ing => ({
        ingrediente_id: ing.ingrediente_id,
        ingrediente_nome: ing.ingrediente_nome,
        quantita_grammi: ing.quantita,
        quantita_originale: ing.quantita,
        quantita_input: String(fromSmall(ing.quantita, ing.unita_misura || "g")),
        unita_misura: (ing.unita_misura || "g").toLowerCase(),
        calo_cottura_percentuale: ing.calo_cottura_percentuale || 0,
        lotto_numero: "",
        data_scadenza: "",
        foto_path: null,
        foto_preview: null,
        fattura_immagine_path: "",
        completato: false
      }));
    setIngredientiPrepara(ingredienti);
    setCurrentIngredienteIndex(0);
    setStep('ingredienti');
  };

  const iniziaPreparazioneManuale = () => {
    setRicettaSelezionata(null);
    setNomePreparazione("");
    setIngredientiPrepara([]);
    setCurrentIngredienteIndex(0);
    setStep('ingredienti');
  };

  // --- Ricette Light ---
  const addLightComponente = () => {
    setLightComponenti(prev => [...prev, { nome: "", ingrediente_id: "", quantita: "", unita: "g" }]);
  };

  const updateLightComponente = (idx, field, value) => {
    setLightComponenti(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const selectLightIngrediente = (idx, ingId) => {
    const ing = ingredientiDisponibili.find(i => i.id === ingId);
    if (!ing) return;
    setLightComponenti(prev => prev.map((c, i) => i === idx
      ? { ...c, ingrediente_id: ing.id, nome: ing.nome, unita: c.unita || ing.unita_misura || "g" }
      : c));
  };

  const removeLightComponente = (idx) => {
    setLightComponenti(prev => prev.filter((_, i) => i !== idx));
  };

  const salvaRicettaLightEPrepara = async () => {
    if (!lightNome.trim()) { toast.error("Inserisci il nome della ricetta"); return; }
    const validi = lightComponenti.filter(c => c.nome.trim() && parseFloat(c.quantita) > 0 && (!hasAcquisti || c.ingrediente_id));
    if (validi.length === 0) {
      toast.error(hasAcquisti ? "Aggiungi almeno un ingrediente dal catalogo con quantità" : "Aggiungi almeno un componente");
      return;
    }
    setSavingLight(true);
    try {
      const ingredienti = validi.map(c => ({
        ingrediente_id: hasAcquisti ? (c.ingrediente_id || "") : "",
        ingrediente_nome: c.nome.trim(),
        tipo: "ingrediente",
        ricetta_id: null,
        quantita: hasAcquisti ? toSmall(c.quantita, c.unita) : parseFloat(c.quantita),
        peso_lordo: null,
        percentuale_scarto: 0,
        unita_misura: c.unita || "g",
        calo_cottura_percentuale: 0,
        is_acqua: false,
        prezzo_usato: 0,
        costo_ingrediente: 0
      }));
      const payload = {
        locale_id: localeId,
        nome: lightNome.trim(),
        ingredienti,
        // Con Acquisti → ricetta COMPLETA (food cost calcolato dal backend); senza → light
        is_light: !hasAcquisti,
        richiede_tracciabilita: true,
      };
      const res = await axios.post(`${API}/ricette`, payload, { headers: getAuthHeaders() });
      toast.success(`Ricetta "${lightNome}" salvata!`);
      // Seleziona la ricetta appena creata e procedi con il flusso Prepara
      const nuovaRicetta = res.data;
      setLightMode(false);
      selezionaRicetta(nuovaRicetta);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingLight(false);
    }
  };

  const aggiungiIngrediente = (ingredienteId) => {
    const ing = ingredientiDisponibili.find(i => i.id === ingredienteId);
    if (!ing) return;
    setIngredientiPrepara(prev => [...prev, {
      ingrediente_id: ing.id, ingrediente_nome: ing.nome,
      quantita_grammi: 0, quantita_originale: 0,
      unita_misura: (ing.unita_misura || "g").toLowerCase(),
      calo_cottura_percentuale: ing.calo_cottura_percentuale || 0,
      lotto_numero: "", data_scadenza: "",
      foto_path: null, foto_preview: null,
      fattura_immagine_path: "",
      completato: false
    }]);
  };

  const rimuoviIngrediente = (index) => {
    setIngredientiPrepara(prev => prev.filter((_, i) => i !== index));
    if (currentIngredienteIndex >= ingredientiPrepara.length - 1) {
      setCurrentIngredienteIndex(Math.max(0, ingredientiPrepara.length - 2));
    }
  };

  // Upload foto immediato via /api/upload-image (payload leggero al salvataggio)
  const uploadFoto = useCallback(async (base64Data) => {
    setUploadingPhoto(true);
    try {
      // Convert base64 to blob for FormData upload
      const resp = await fetch(base64Data);
      const blob = await resp.blob();
      const formData = new FormData();
      formData.append("file", new File([blob], "photo.webp", { type: "image/webp" }));
      const uploadRes = await axios.post(
        `${API}/upload-image?locale_id=${localeId || "default"}`, 
        formData, 
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      return uploadRes.data.path;
    } catch (e) {
      console.error("Upload foto fallito:", e);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  }, [localeId, getAuthHeaders]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { compressToWebP } = await import("../utils/imageCompressor");
      // Comprimi e ridimensiona a max 1200px per velocità
      const { preview } = await compressToWebP(file, 0.8, 1200);
      setFotoBase64(preview);
      setFotoDialogOpen(true);
    } catch {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFotoBase64(event.target.result);
        setFotoDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const scattaFoto = () => { fileInputRef.current?.click(); };

  const confermaFoto = async (mode) => {
    if (mode === 'scan') {
      setOcrLoading(true);
      try {
        const b64Only = fotoBase64.includes(',') ? fotoBase64.split(',')[1] : fotoBase64;
        const response = await axios.post(
          `${API}/ocr/lotto`,
          { images_base64: [b64Only] },
          { headers: getAuthHeaders() }
        );
        const { numero_lotto, data_scadenza } = response.data;
        
        // Upload foto in background
        const uploadedPath = await uploadFoto(fotoBase64);
        
        setIngredientiPrepara(prev => prev.map((ing, idx) => 
          idx === currentIngredienteIndex
            ? { 
                ...ing, 
                lotto_numero: numero_lotto || ing.lotto_numero, 
                data_scadenza: data_scadenza || ing.data_scadenza, 
                foto_path: uploadedPath,
                foto_preview: fotoBase64,
                completato: !!(numero_lotto && data_scadenza) 
              }
            : ing
        ));
        const msgs = [];
        if (numero_lotto) msgs.push(`Lotto: ${numero_lotto}`);
        if (data_scadenza) msgs.push(`Scadenza: ${data_scadenza}`);
        if (msgs.length > 0) toast.success(`Dati estratti: ${msgs.join(' | ')}`);
        else toast.warning("OCR non ha trovato lotto/scadenza - inserisci manualmente");
      } catch {
        toast.error("Errore OCR, inserisci i dati manualmente");
        const uploadedPath = await uploadFoto(fotoBase64);
        setIngredientiPrepara(prev => prev.map((ing, idx) => 
          idx === currentIngredienteIndex 
            ? { ...ing, foto_path: uploadedPath, foto_preview: fotoBase64 } 
            : ing
        ));
      } finally {
        setOcrLoading(false);
      }
    } else {
      // Solo salva foto - upload immediato
      const uploadedPath = await uploadFoto(fotoBase64);
      setIngredientiPrepara(prev => prev.map((ing, idx) => 
        idx === currentIngredienteIndex 
          ? { ...ing, foto_path: uploadedPath, foto_preview: fotoBase64 } 
          : ing
      ));
      toast.success("Foto salvata");
    }
    setFotoDialogOpen(false);
    setFotoBase64(null);
  };

  const updateIngrediente = (index, field, value) => {
    setIngredientiPrepara(prev => prev.map((ing, idx) => {
      if (idx !== index) return ing;
      if (field === 'quantita_grammi') {
        return updateQuantityInput(ing, value);
      }
      return { ...ing, [field]: value };
    }));
  };

  const prossimoIngrediente = () => {
    if (currentIngredienteIndex < ingredientiPrepara.length - 1) {
      setCurrentIngredienteIndex(prev => prev + 1);
    } else {
      // Calcola data scadenza di default: la più imminente tra gli ingredienti
      const scadenze = ingredientiPrepara
        .map(ing => ing.data_scadenza)
        .filter(d => d && d.trim());
      if (scadenze.length > 0) {
        const minScadenza = scadenze.reduce((min, curr) => curr < min ? curr : min);
        setDataScadenzaLotto(minScadenza);
        // Pre-seleziona la durata corrispondente
        const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
        const scad = new Date(minScadenza); scad.setHours(0, 0, 0, 0);
        const diff = Math.ceil((scad - oggi) / (1000 * 60 * 60 * 24));
        setDurataGiorni([1,2,3,5,7,10,14,15,20,30].includes(diff) ? String(diff) : "custom");
      }
      setStep('salva');
    }
  };

  // Controlla se le quantità sono cambiate rispetto alla ricetta originale
  const hasQuantityChanges = () => {
    if (!ricettaSelezionata) return false;
    return ingredientiPrepara.some(ing => 
      ing.quantita_originale > 0 && 
      parseFloat(ing.quantita_grammi) !== parseFloat(ing.quantita_originale)
    );
  };

  // Completa la preparazione (chiamata finale)
  const completaPreparazione = async (saveMode = 'skip') => {
    if (loading) return;
    if (ricettaSelezionata && ricettaSelezionata.locale_id !== localeId) {
      setRecipeContextError("Il locale è cambiato durante la preparazione. Controlla la sede selezionata; i dati inseriti non sono stati salvati.");
      return;
    }
    if (!nomePreparazione.trim()) { toast.error("Inserisci un nome"); return; }
    // Check ingredienti: tutti devono avere lotto + scadenza (o almeno la foto)
    const senzaDati = ingredientiPrepara.filter(ing => !ing.lotto_numero?.trim() || !ing.data_scadenza?.trim());
    const senzaNulla = ingredientiPrepara.filter(ing => !isIngredienteCompleto(ing));
    if (senzaNulla.length > 0) {
      toast.error(`${senzaNulla.length} ingredienti incompleti - servono lotto e scadenza`);
      return;
    }
    if (senzaDati.length > 0 && senzaDati.some(ing => !ing.foto_path)) {
      toast.error(`Inserisci lotto e data scadenza per tutti gli ingredienti (o scatta la foto)`);
      return;
    }
    setLoading(true);
    setRecipeContextError(null);
    try {
      const payload = {
        locale_id: localeId, 
        ricetta_id: ricettaSelezionata?.id || null, 
        nome: nomePreparazione,
        data_scadenza_lotto: null,
        ingredienti: ingredientiPrepara.map(ing => ({
          ingrediente_id: ing.ingrediente_id, 
          ingrediente_nome: ing.ingrediente_nome,
          quantita_grammi: parseFloat(ing.quantita_grammi), 
          lotto_numero: ing.lotto_numero,
          data_scadenza: ing.data_scadenza, 
          foto_path: ing.foto_path,
          fattura_immagine_path: ing.fattura_immagine_path || null,
          lotto_origine_id: ing.lotto_origine_id || null,
          unita_misura: ing.unita_misura || "g",
          calo_cottura_percentuale: parseFloat(ing.calo_cottura_percentuale) || 0
        })),
        salva_come_ricetta: saveMode === 'overwrite' || saveMode === 'new',
        sovrascrivi_ricetta_id: saveMode === 'overwrite' ? ricettaSelezionata?.id : null,
        operatore: operatore, 
        note: "",
        quantita_preparata_override: quantitaPreparata ? parseFloat(quantitaPreparata) : null,
        unita_misura_lotto: unitaMisuraLotto || "kg",
      };
      const response = await axios.post(`${API}/preparazioni`, payload, { 
        headers: getAuthHeaders(),
        timeout: 60000 
      });
      toast.success(`Preparazione ${response.data.codice} creata!`);
      if (saveMode === 'overwrite') toast.success("Ricetta aggiornata con le nuove quantità");
      if (saveMode === 'new') toast.success("Nuova ricetta salvata");
      onSuccess?.({ id: response.data.lotto_id, nome: response.data.nome_prodotto || ricettaSelezionata?.nome,
        lotto: response.data.lotto, data_scadenza: response.data.lotto?.data_scadenza || "" });
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - riprova con meno foto o connessione migliore");
      } else if (error.response?.status === 404 && typeof detail === 'string' &&
                 detail.includes('Ricetta') && detail.includes('non trovata in questo locale')) {
        setRecipeContextError("La ricetta non è disponibile per il locale selezionato. Nessun lotto è stato salvato; i dati inseriti sono ancora qui.");
      } else {
        toast.error(detail || "Errore nella creazione della preparazione");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler per il pulsante principale di salvataggio
  const handleSaveClick = () => {
    if (!ricettaSelezionata) {
      // Creazione da zero → chiedi se vuole salvare come nuova ricetta
      if (ingredientiPrepara.length >= 2) {
        setPendingAction('ask_new');
        setStep('confirm_recipe');
      } else {
        completaPreparazione('skip');
      }
    } else if (hasQuantityChanges() && ricettaSelezionata.is_light && !isAdmin && !hasAcquisti) {
      // Gli operatori possono preparare il lotto con dosi diverse, senza cambiare la ricetta Light.
      completaPreparazione('skip');
    } else if (hasQuantityChanges()) {
      // Ha modificato le quantità della ricetta → chiedi se sovrascrivere
      setPendingAction('ask_overwrite');
      setStep('confirm_recipe');
    } else {
      // Nessuna modifica alle quantità → salva direttamente
      completaPreparazione('skip');
    }
  };

  const currentIng = ingredientiPrepara[currentIngredienteIndex];
  const lottiCorrenti = lottiDisponibiliPerIngrediente(lottiDisponibili, currentIng);
  const completati = ingredientiPrepara.filter(isIngredienteCompleto).length;

  const selezionaLottoEsistente = (lotto, quantita, nomeLotto) => {
    const scadenza = lotto.data_scadenza_aperto || lotto.data_scadenza || "";
    setIngredientiPrepara(prev => prev.map((ing, idx) => idx === currentIngredienteIndex ? {
      ...ing,
      lotto_origine_id: lotto.id,
      lotto_origine_quantita_base: quantita.quantitaBase,
      lotto_origine_disponibile: `${quantita.valore} ${quantita.unita}`,
      lotto_origine_nome: nomeLotto,
      lotto_numero: lotto.numero_lotto || lotto.interno_lotto || "",
      data_scadenza: scadenza,
      foto_path: lotto.immagine_path || null,
      foto_preview: lotto.immagine_path ? fileUrl(lotto.immagine_path) : null,
      fattura_immagine_path: lotto.fattura_immagine_path || "",
    } : ing));
    setLottiDialogOpen(false);
    toast.success("Lotto madre selezionato");
  };

  const rimuoviLottoEsistente = () => {
    setIngredientiPrepara(prev => prev.map((ing, idx) => idx === currentIngredienteIndex ? {
      ...ing,
      lotto_origine_id: null,
      lotto_origine_quantita_base: null,
      lotto_origine_disponibile: null,
      lotto_origine_nome: null,
      lotto_numero: "",
      data_scadenza: "",
      foto_path: null,
      foto_preview: null,
      fattura_immagine_path: "",
    } : ing));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="w-full h-[100dvh] max-w-none sm:max-w-lg sm:h-auto sm:max-h-[90vh] m-0 sm:mx-auto p-0 rounded-none sm:rounded-3xl bg-card border-0 sm:border sm:border-border flex flex-col" data-testid="prepara-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Prepara un nuovo lotto</DialogTitle>
            <DialogDescription>Seleziona la ricetta e completa la tracciabilità degli ingredienti.</DialogDescription>
          </DialogHeader>
          
          {/* Header fisso */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-primary" />
              <h2 className="text-foreground font-bold text-lg" data-testid="prepara-title">{step === 'trash' ? 'Cestino ricette' : step === 'edit-light' ? 'Modifica ricetta Light' : 'Prepara'}</h2>
              {step === 'ingredienti' && currentIng && (
                <span className="text-muted-foreground text-sm">
                  {currentIngredienteIndex + 1}/{ingredientiPrepara.length}
                </span>
              )}
            </div>
            {step === 'ingredienti' && ingredientiPrepara.length > 0 && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                {completati}/{ingredientiPrepara.length} completati
              </span>
            )}
          </div>

          {/* Contenuto scrollabile */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {recipeContextError && (
              <p role="alert" data-testid="prepara-recipe-context-error" className="border border-red-300 bg-red-50 text-red-800 text-sm p-3 my-3 rounded-md">
                {recipeContextError}
              </p>
            )}

            {/* STEP 1: Selezione ricetta */}
            {step === 'select' && (
              <div className="space-y-3">
                {!lightMode ? (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-muted-foreground text-sm">Seleziona una ricetta o creane una nuova</p>
                      {isAdmin && !hasAcquisti && (
                        <Button variant="ghost" size="sm" onClick={openLightTrash} className="shrink-0" data-testid="light-open-trash">
                          <Trash2 className="h-4 w-4 mr-1" />Cestino
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {ricette.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Nessuna ricetta salvata</p>
                          <p className="text-xs mt-1">{hasAcquisti ? "Crea la tua prima ricetta per iniziare" : "Crea la tua prima ricetta light per iniziare"}</p>
                        </div>
                      ) : (
                        ricette.map(ricetta => (
                          <div key={ricetta.id} className="flex items-stretch bg-muted rounded-xl border border-border min-w-0">
                          <button
                            key={ricetta.id}
                            onClick={() => selezionaRicetta(ricetta)}
                            className="flex-1 min-w-0 p-3 hover:bg-muted/80 rounded-xl text-left transition-colors"
                            data-testid={`ricetta-${ricetta.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-foreground font-medium text-sm truncate">{ricetta.nome}</h3>
                                  {ricetta.is_light ? (
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30" data-testid={`recipe-light-badge-${ricetta.id}`}>LIGHT</span>
                                  ) : (
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-500 border border-emerald-500/30">COMPLETA</span>
                                  )}
                                </div>
                                <p className="text-muted-foreground text-xs mt-0.5">
                                  {ricetta.ingredienti?.filter(i => !i.is_acqua).length || 0} ingredienti
                                </p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            </div>
                          </button>
                          {isAdmin && !hasAcquisti && ricetta.is_light && (
                            <div className="flex items-center pr-2 gap-0.5 shrink-0">
                              <Button variant="ghost" size="icon" title={`Modifica ${ricetta.nome}`} aria-label={`Modifica ${ricetta.nome}`}
                                onClick={() => { setEditorRecipe(ricetta); setStep('edit-light'); }} data-testid={`light-edit-recipe-${ricetta.id}`}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" title={`Elimina ${ricetta.nome}`} aria-label={`Elimina ${ricetta.nome}`}
                                onClick={() => setArchiveTarget(ricetta)} data-testid={`light-archive-recipe-${ricetta.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          )}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="pt-3 border-t border-border space-y-2">
                      <Button onClick={() => { setLightMode(true); setLightComponenti([{ nome: "", ingrediente_id: "", quantita: "", unita: "g" }]); }} className={`w-full font-bold ${hasAcquisti ? "bg-primary hover:bg-primary/90 text-white" : "bg-amber-500 hover:bg-amber-600 text-black"}`} data-testid="crea-light-btn">
                        <Zap className="h-4 w-4 mr-2" />
                        {hasAcquisti ? "Crea Ricetta" : "Crea Ricetta Light"}
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Form creazione ricetta inline (light o completa in base al pacchetto) */
                  <RicettaInlineForm
                    hasAcquisti={hasAcquisti}
                    lightNome={lightNome}
                    setLightNome={setLightNome}
                    lightComponenti={lightComponenti}
                    ingredientiDisponibili={ingredientiDisponibili}
                    savingLight={savingLight}
                    onBack={() => setLightMode(false)}
                    onAddComponente={addLightComponente}
                    onUpdateComponente={updateLightComponente}
                    onSelectIngrediente={selectLightIngrediente}
                    onRemoveComponente={removeLightComponente}
                    onSave={salvaRicettaLightEPrepara}
                  />
                )}
              </div>
            )}

            {step === 'trash' && isAdmin && !hasAcquisti && (
              <LightRecipeTrash items={trashedRecipes} loading={trashLoading} restoringId={restoringId}
                onBack={() => setStep('select')} onRestore={restoreLight} />
            )}

            {step === 'edit-light' && isAdmin && !hasAcquisti && editorRecipe && (
              <LightRecipeEditorDialog recipe={editorRecipe} localeId={localeId} getAuthHeaders={getAuthHeaders}
                onClose={() => { setEditorRecipe(null); setStep('select'); }} onSaved={fetchData} />
            )}

            {/* STEP 2: Ingredienti */}
            {step === 'ingredienti' && (
              <div className="space-y-3">
                {!ricettaSelezionata && (
                  <Input
                    value={nomePreparazione}
                    onChange={(e) => setNomePreparazione(e.target.value)}
                    placeholder="Nome preparazione..."
                    className="h-10 bg-card border-border text-foreground"
                    data-testid="nome-preparazione"
                  />
                )}
                
                {!ricettaSelezionata && (
                  <Select onValueChange={aggiungiIngrediente}>
                    <SelectTrigger className="bg-card border-border text-foreground h-10">
                      <SelectValue placeholder="+ Aggiungi ingrediente..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-60">
                      {ingredientiDisponibili
                        .filter(i => !ingredientiPrepara.find(p => p.ingrediente_id === i.id))
                        .map(i => (
                          <SelectItem key={i.id} value={i.id} className="text-foreground">{i.nome}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                )}
                
                {/* Chip ingredienti */}
                {ingredientiPrepara.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pb-1 -mx-1 px-1">
                    {ingredientiPrepara.map((ing, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentIngredienteIndex(idx)}
                        className={`max-w-full min-w-0 px-2.5 py-1 rounded-full text-xs font-medium truncate flex items-center gap-1 transition-colors ${
                          idx === currentIngredienteIndex
                            ? 'bg-primary text-white'
                            : isIngredienteCompleto(ing)
                              ? 'bg-green-500/20 !text-green-900 border border-green-500/40'
                              : 'bg-muted text-muted-foreground'
                        }`}
                        data-testid={`chip-ing-${idx}`}
                      >
                        {isIngredienteCompleto(ing) && <Check className="h-3 w-3" />}
                        {ing.ingrediente_nome}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Form ingrediente corrente */}
                {currentIng && (
                  <div className="bg-muted rounded-2xl border border-border p-3 space-y-3" data-testid="current-ingredient-form">
                    <div className="flex items-center justify-between">
                      <h3 className="text-foreground font-bold">{currentIng.ingrediente_nome}</h3>
                      {!ricettaSelezionata && (
                        <button onClick={() => rimuoviIngrediente(currentIngredienteIndex)} className="text-red-400 p-1">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {!currentIng.lotto_origine_id && lottiCorrenti.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setLottiDialogOpen(true)}
                        className="group w-full justify-between border-emerald-400 bg-emerald-50 text-emerald-900 hover:border-emerald-700 hover:bg-emerald-700 hover:text-white focus-visible:border-emerald-700 focus-visible:bg-emerald-700 focus-visible:text-white active:bg-emerald-800 active:text-white"
                        data-testid="open-existing-lots-button"
                      >
                        <span className="inline-flex items-center gap-2">
                          <PackageCheck className="h-4 w-4" /> Seleziona da lotti esistenti
                        </span>
                        <span className="rounded bg-emerald-700 px-2 py-0.5 text-xs text-white transition-colors group-hover:bg-white group-hover:text-emerald-800 group-focus-visible:bg-white group-focus-visible:text-emerald-800" data-testid="existing-lots-count">
                          {lottiCorrenti.length}
                        </span>
                      </Button>
                    )}

                    {currentIng.lotto_origine_id && (
                      <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid="selected-existing-lot-card">
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-800" data-testid="selected-existing-lot-number">
                            <PackageCheck className="h-4 w-4" /> Lotto madre {currentIng.lotto_numero}
                          </p>
                          {currentIng.lotto_origine_nome && (
                            <p className="mt-1 text-sm font-semibold text-emerald-900" data-testid="selected-existing-lot-product-name">
                              {currentIng.lotto_origine_nome}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-emerald-700" data-testid="selected-existing-lot-available">
                            Disponibile {currentIng.lotto_origine_disponibile} · scarico automatico al salvataggio
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={rimuoviLottoEsistente}
                          className="shrink-0 text-xs font-bold text-emerald-800 underline underline-offset-2"
                          data-testid="remove-existing-lot-button"
                        >
                          Usa foto/dati
                        </button>
                      </div>
                    )}
                    
                    <div className="flex gap-2 items-center">
                      {currentIng.foto_preview ? (
                        <div className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0">
                          <img src={currentIng.foto_preview} alt="" className="w-full h-full object-cover" />
                          {uploadingPhoto && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Loader2 className="h-5 w-5 text-white animate-spin" />
                            </div>
                          )}
                          {!currentIng.lotto_origine_id && (
                            <button
                              onClick={() => { updateIngrediente(currentIngredienteIndex, 'foto_path', null); updateIngrediente(currentIngredienteIndex, 'foto_preview', null); }}
                              className="absolute top-0.5 right-0.5 h-5 w-5 bg-black/60 rounded-full flex items-center justify-center"
                              data-testid="remove-ingredient-photo-button"
                            >
                              <X className="h-3 w-3 text-white" />
                            </button>
                          )}
                        </div>
                      ) : currentIng.lotto_origine_id ? (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="selected-lot-no-photo">
                          <PackageCheck className="h-6 w-6" />
                        </div>
                      ) : (
                        <button
                          onClick={scattaFoto}
                          className="w-16 h-16 rounded-xl bg-card border-2 border-dashed border-border flex flex-col items-center justify-center shrink-0"
                          data-testid="scatta-foto-btn"
                        >
                          <Camera className="h-5 w-5 text-muted-foreground" />
                          <span className="text-[9px] text-muted-foreground mt-0.5">Foto</span>
                        </button>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
                      
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Lotto *</label>
                          <Input
                            value={currentIng.lotto_numero}
                            onChange={(e) => updateIngrediente(currentIngredienteIndex, 'lotto_numero', e.target.value)}
                            disabled={!!currentIng.lotto_origine_id}
                            placeholder="es. L12345"
                            className={`h-9 text-sm bg-card ${!currentIng.lotto_numero?.trim() && !currentIng.foto_path ? 'border-red-500/60 focus:border-red-500' : 'border-border'}`}
                            data-testid="input-lotto"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Scadenza *</label>
                          <div className={currentIng.lotto_origine_id ? "pointer-events-none opacity-70" : ""}>
                            <DateInput value={currentIng.data_scadenza} onChange={(v) => updateIngrediente(currentIngredienteIndex, 'data_scadenza', v)} disablePast />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        {(() => {
                          const um = currentIng.unita_misura || "g";
                          const umLabels = { g: "Grammi", kg: "Kg", pz: "Pezzi", lt: "Litri", cl: "Cl", ml: "Ml" };
                          const umText = umLabels[um] || um.toUpperCase();
                          return (
                            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                              {umText} {currentIng.quantita_originale > 0 && <span data-testid="prepara-original-quantity">(ricetta: {formatSmallQuantity(currentIng.quantita_originale, um)})</span>}
                            </label>
                          );
                        })()}
                        <Input
                          type="number" inputMode="decimal"
                          step="any" min="0"
                          value={currentIng.quantita_input ?? String(fromSmall(currentIng.quantita_grammi, currentIng.unita_misura))}
                          onChange={(e) => updateIngrediente(currentIngredienteIndex, 'quantita_grammi', e.target.value)}
                          placeholder="500"
                          className="h-9 text-sm bg-card border-border"
                          data-testid="input-quantita"
                        />
                        {currentIng.lotto_origine_id && Number(currentIng.quantita_grammi || 0) > Number(currentIng.lotto_origine_quantita_base || 0) && (
                          <p className="mt-1 text-xs font-medium text-red-600" data-testid="selected-lot-insufficient-warning">
                            Quantità superiore alla disponibilità del lotto madre.
                          </p>
                        )}
                      </div>
                      {/* Spacer */}
                    </div>

                    {/* Carica Fattura (facoltativa per singolo ingrediente) */}
                    {currentIng.fattura_immagine_path ? (
                      <div
                        className="w-full flex items-center gap-3 p-2 rounded-xl border border-indigo-200 bg-indigo-50/60"
                        data-testid="prepara-fattura-preview"
                      >
                        <img
                          src={getFatturaPreview(currentIng.fattura_immagine_path) || fileUrl(currentIng.fattura_immagine_path)}
                          alt="Fattura selezionata"
                          data-testid="prepara-fattura-thumbnail"
                          className="w-12 h-12 object-cover rounded-lg border border-indigo-200 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-indigo-700 flex items-center gap-1">
                            <Check className="h-3 w-3" /> Fattura allegata a questo ingrediente
                          </p>
                          <p className="text-[10px] text-indigo-600/80 truncate">Verrà mostrata nel QR pubblico.</p>
                        </div>
                        {!currentIng.lotto_origine_id && (
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() => { fetchFatturaStorico(); setFatturaSheetOpen(true); }}
                              className="text-[11px] font-bold text-indigo-700 hover:underline"
                              data-testid="prepara-fattura-change-btn"
                            >
                              Cambia
                            </button>
                            <button
                              type="button"
                              onClick={handleFatturaRemove}
                              className="text-[11px] font-bold text-red-600 hover:underline"
                              data-testid="prepara-fattura-remove-btn"
                            >
                              Rimuovi
                            </button>
                          </div>
                        )}
                      </div>
                    ) : !currentIng.lotto_origine_id ? (
                      <button
                        type="button"
                        onClick={() => { fetchFatturaStorico(); setFatturaSheetOpen(true); }}
                        className="w-full h-10 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border-2 border-dashed border-indigo-300 bg-indigo-50/40 text-indigo-700 hover:bg-indigo-50 transition-colors"
                        data-testid="prepara-carica-fattura-btn"
                      >
                        <FileText className="h-4 w-4" />
                        <span>Carica Fattura</span>
                        <span className="text-[10px] font-normal text-indigo-500/80">(facoltativo)</span>
                      </button>
                    ) : null}
                    <input
                      ref={fatturaFileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFatturaNuovoScatto}
                      className="hidden"
                      data-testid="prepara-fattura-file-input"
                    />
                  </div>
                )}
                
                <div className="flex gap-2">
                  {currentIngredienteIndex > 0 && (
                    <Button variant="outline" onClick={() => setCurrentIngredienteIndex(prev => prev - 1)} className="flex-1 h-11 border-border text-foreground">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
                    </Button>
                  )}
                  <Button
                    onClick={prossimoIngrediente}
                    disabled={!currentIng || !isIngredienteCompleto(currentIng)}
                    className="flex-1 h-11 bg-primary hover:bg-primary/90 text-white disabled:opacity-50"
                    data-testid="next-ingredient-btn"
                  >
                    {currentIngredienteIndex < ingredientiPrepara.length - 1
                      ? <>Prossimo <ChevronRight className="h-4 w-4 ml-1" /></>
                      : <>Continua <ChevronRight className="h-4 w-4 ml-1" /></>
                    }
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Riepilogo e Salva */}
            {step === 'salva' && (
              <div className="space-y-3">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                  <h3 className="text-primary font-bold text-sm mb-1">Riepilogo</h3>
                  <p className="text-muted-foreground text-sm">
                    <strong className="text-foreground">{nomePreparazione}</strong> — {ingredientiPrepara.length} ingredienti
                  </p>
                  {ricettaSelezionata && hasQuantityChanges() && (
                    <p className="text-amber-500 text-xs mt-1 flex items-center gap-1">
                      <Scale className="h-3 w-3" /> Quantita modificate rispetto alla ricetta
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Nome</label>
                  <Input value={nomePreparazione} onChange={(e) => setNomePreparazione(e.target.value)} className="h-10 bg-card border-border text-foreground" data-testid="nome-riepilogo" />
                </div>

                {/* Quantità realmente preparata */}
                <div className="bg-teal-500/5 rounded-2xl border border-teal-500/30 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-teal-600" />
                    <p className="text-xs font-bold text-foreground uppercase tracking-wide">Quantità preparata</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Stima dagli ingredienti: <strong className="text-foreground">{pesoFinaleCalcolatoKg} kg</strong>.
                    Modifica se hai preparato una quantità diversa.
                  </p>
                  <div className="grid grid-cols-[1fr_120px] gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={quantitaPreparata}
                      onChange={(e) => { setQuantitaPreparata(e.target.value); setQpManuallyEdited(true); }}
                      placeholder="0"
                      className="h-11 bg-card border-border text-foreground text-lg font-bold"
                      data-testid="quantita-preparata-input"
                    />
                    <Select
                      value={unitaMisuraLotto}
                      onValueChange={(v) => { setUnitaMisuraLotto(v); setQpManuallyEdited(false); }}
                      data-testid="udm-lotto-select"
                    >
                      <SelectTrigger className="h-11 bg-card border-border text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="kg" className="text-foreground">Kg</SelectItem>
                        <SelectItem value="g" className="text-foreground">g</SelectItem>
                        <SelectItem value="lt" className="text-foreground">Lt</SelectItem>
                        <SelectItem value="ml" className="text-foreground">ml</SelectItem>
                        <SelectItem value="pz" className="text-foreground">Pz</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Lista ingredienti riepilogo */}
                <div className="space-y-1.5">
                  {ingredientiPrepara.map((ing, idx) => {
                    const mancaDati = !ing.lotto_numero?.trim() || !ing.data_scadenza?.trim();
                    const haFoto = !!ing.foto_path;
                    return (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${mancaDati && !haFoto ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted'}`}>
                      {ing.foto_preview && (
                        <img src={ing.foto_preview} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="flex-1 text-foreground font-medium truncate">{ing.ingrediente_nome}</span>
                      {mancaDati && !haFoto && <span className="text-red-500 text-[10px] font-bold">INCOMPLETO</span>}
                      {ing.lotto_origine_id && (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`summary-source-lot-${idx}`}>
                          LOTTO MADRE
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs" data-testid={`summary-ingredient-quantity-${idx}`}>
                        {formatSmallQuantity(ing.quantita_grammi, ing.unita_misura)}
                      </span>
                    </div>
                    );
                  })}
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setStep('ingredienti'); setCurrentIngredienteIndex(ingredientiPrepara.length - 1); }} className="flex-1 h-11 border-border text-foreground">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
                  </Button>
                  <Button 
                    onClick={handleSaveClick} 
                    disabled={loading || !nomePreparazione.trim()} 
                    className="flex-1 h-11 bg-primary hover:bg-primary/90 text-white" 
                    data-testid="completa-btn"
                  >
                    {loading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</>
                    ) : (
                      "Salva Lotto"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 4: Conferma ricetta */}
            {step === 'confirm_recipe' && pendingAction === 'ask_overwrite' && (
              <div className="space-y-4 py-4">
                <div className="text-center">
                  <div className="w-14 h-14 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Save className="h-7 w-7 text-amber-500" />
                  </div>
                  <h3 className="text-foreground font-bold text-base mb-2">Quantita modificate</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Vuoi sovrascrivere la ricetta esistente "<strong className="text-foreground">{ricettaSelezionata?.nome}</strong>" con le nuove quantita?
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    La prossima volta che creerai un lotto di questa ricetta, userà le nuove quantita.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Button 
                    onClick={() => setConfirmOverwriteOpen(true)}
                    className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-medium"
                    data-testid="overwrite-recipe-btn"
                  >
                    <Save className="h-4 w-4 mr-2" /> Sovrascrivi ricetta e salva lotto
                  </Button>
                  <Button 
                    onClick={() => completaPreparazione('skip')}
                    disabled={loading}
                    variant="outline" 
                    className="w-full h-12 border-border text-foreground"
                    data-testid="skip-recipe-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva solo il lotto"}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setStep('salva')} 
                    className="w-full h-10 text-muted-foreground"
                    data-testid="prepara-overwrite-back-button"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Torna al riepilogo
                  </Button>
                </div>
              </div>
            )}

            {step === 'confirm_recipe' && pendingAction === 'ask_new' && (
              <div className="space-y-4 py-4">
                <div className="text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <BookOpen className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-foreground font-bold text-base mb-2">Salvare la Nuova Ricetta?</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Vuoi salvare "<strong className="text-foreground">{nomePreparazione}</strong>" come ricetta riutilizzabile?
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    La prossima volta potrai selezionarla per creare un nuovo lotto più velocemente.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Button 
                    onClick={() => completaPreparazione('new')}
                    disabled={loading}
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium"
                    data-testid="save-new-recipe-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Salva Nuova Ricetta & Crea Lotto</>}
                  </Button>
                  <Button 
                    onClick={() => completaPreparazione('skip')}
                    disabled={loading}
                    variant="outline" 
                    className="w-full h-12 border-border text-foreground"
                    data-testid="skip-new-recipe-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva solo il lotto"}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setStep('salva')} 
                    className="w-full h-10 text-muted-foreground"
                    data-testid="prepara-new-recipe-back-button"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Torna al riepilogo
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={value => { if (!value && !archivingLight) setArchiveTarget(null); }}>
        <AlertDialogContent data-testid="light-archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare «{archiveTarget?.nome}»?</AlertDialogTitle>
            <AlertDialogDescription>La ricetta non apparirà più in Prepara. I lotti già creati restano intatti e potrai ripristinare la ricetta dal Cestino.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archivingLight} data-testid="light-archive-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={event => { event.preventDefault(); archiveLight(); }} disabled={archivingLight} data-testid="light-archive-confirm">
              {archivingLight ? 'Eliminazione...' : 'Elimina ricetta'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LottiEsistentiDialog
        open={lottiDialogOpen}
        onOpenChange={setLottiDialogOpen}
        ingrediente={currentIng}
        items={lottiCorrenti}
        ingredientiCatalogo={ingredientiDisponibili}
        localeId={localeId}
        onSelect={selezionaLottoEsistente}
      />

      {/* Dialog scelta foto */}
      <FotoSceltaDialog
        open={fotoDialogOpen}
        onOpenChange={setFotoDialogOpen}
        fotoBase64={fotoBase64}
        ocrLoading={ocrLoading}
        uploadingPhoto={uploadingPhoto}
        onConferma={confermaFoto}
      />

      {/* Modal conferma sovrascrittura ricetta */}
      <ConfermaSovrascritturaDialog
        open={confirmOverwriteOpen}
        onOpenChange={setConfirmOverwriteOpen}
        ricettaNome={ricettaSelezionata?.nome}
        loading={loading}
        onConferma={() => { setConfirmOverwriteOpen(false); completaPreparazione('overwrite'); }}
      />

      {/* Sheet Carica Fattura per il singolo ingrediente della ricetta */}
      <FatturaSheet
        open={fatturaSheetOpen}
        onOpenChange={setFatturaSheetOpen}
        storico={fatturaStorico}
        localPreviews={fatturaPreviewUrls}
        onSelectStorico={handleFatturaSelectFromStorico}
        onNuovoScatto={() => fatturaFileInputRef.current?.click()}
        uploading={fatturaUploading}
      />
    </>
  );
};

export default PreparaDialog;
